/**
 * Sidebar (chat conversations list) — fix multi-conversation flow.
 *
 * Trước: button "Hội thoại mới" decoration only, không có delete/rename/search.
 * Sau:
 * - Click "Hội thoại mới" → create + auto-active mới
 * - Hover row → ✏ rename (inline) + 🗑 delete (confirm)
 * - Search filter client-side theo title + preview
 * - Group theo ngày: Hôm nay / Hôm qua / Tuần này / Cũ hơn
 */
import {
  Check,
  MessageSquarePlus,
  PenLine,
  Search,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { conversationsApi, type SearchResultItem } from "@/api/conversations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chat";
import { useUIStore } from "@/stores/ui";
import type { Conversation } from "@/types";

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  const conversations = useChatStore((s) => s.conversations);
  const activeId = useChatStore((s) => s.activeConversationId);
  const setActive = useChatStore((s) => s.setActiveConversation);
  const setConversations = useChatStore((s) => s.setConversations);
  const pushToast = useUIStore((s) => s.pushToast);

  const [filter, setFilter] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editTitle, setEditTitle] = React.useState("");
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    const r = await conversationsApi.list();
    setConversations(r.items);
  }, [setConversations]);

  const handleNew = async (): Promise<void> => {
    setCreating(true);
    try {
      const conv = await conversationsApi.create();
      setConversations([conv, ...conversations]);
      setActive(conv.id);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tạo được hội thoại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setCreating(false);
    }
  };

  const startRename = (conv: Conversation): void => {
    setEditingId(conv.id);
    setEditTitle(conv.title ?? "");
  };

  const cancelRename = (): void => {
    setEditingId(null);
    setEditTitle("");
  };

  const saveRename = async (id: string): Promise<void> => {
    const trimmed = editTitle.trim();
    if (!trimmed) {
      cancelRename();
      return;
    }
    setBusyId(id);
    try {
      await conversationsApi.rename(id, trimmed);
      setConversations(
        conversations.map((c) =>
          c.id === id ? { ...c, title: trimmed } : c,
        ),
      );
      cancelRename();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Đổi tên thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (conv: Conversation): Promise<void> => {
    if (!window.confirm(`Xoá hội thoại "${conv.title ?? conv.id.slice(0, 8)}"?`)) {
      return;
    }
    setBusyId(conv.id);
    try {
      await conversationsApi.archive(conv.id);
      const next = conversations.filter((c) => c.id !== conv.id);
      setConversations(next);
      if (activeId === conv.id) {
        setActive(next[0]?.id ?? null);
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  // Doc 59: server-side search khi q.length > 2 (debounce 300ms)
  const [serverResults, setServerResults] = React.useState<SearchResultItem[] | null>(null);
  const [searching, setSearching] = React.useState(false);

  React.useEffect(() => {
    const q = filter.trim();
    if (q.length < 3) {
      setServerResults(null);
      return;
    }
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const r = await conversationsApi.search(q, 30);
        setServerResults(r.items);
      } catch {
        setServerResults(null);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      setSearching(false);
    };
  }, [filter]);

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversations;

    // Server search results merged với local conversations metadata
    if (serverResults !== null) {
      const localById = new Map(conversations.map((c) => [c.id, c]));
      return serverResults.map((sr) => {
        const local = localById.get(sr.id);
        return {
          // local pass-through (metadata + _matched_field rendering)
          ...(local ?? {}),
          // server data is authoritative
          id: sr.id,
          title: sr.title,
          last_message_at: sr.last_message_at,
          preview: sr.snippet ?? local?.preview ?? null,
        } as Conversation & { _matched_field?: string };
      });
    }

    // Local fallback (instant typing)
    return conversations.filter(
      (c) =>
        (c.title ?? "").toLowerCase().includes(q) ||
        (c.preview ?? "").toLowerCase().includes(q),
    );
  }, [conversations, filter, serverResults]);

  const grouped = React.useMemo(() => groupByDate(filtered), [filtered]);

  return (
    <aside className="hidden md:flex md:flex-col border-r bg-background min-h-0">
      <div className="p-3 border-b space-y-2">
        <Button
          variant="brand"
          className="w-full justify-start gap-2"
          size="sm"
          onClick={() => void handleNew()}
          disabled={creating}
        >
          <MessageSquarePlus className="size-4" />
          {creating ? "Đang tạo..." : "Hội thoại mới"}
        </Button>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Tìm kiếm..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {searching ? (
          <p className="text-xs text-muted-foreground px-2 py-4 text-center">
            Đang tìm trong nội dung tin nhắn...
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-4 text-center">
            {filter
              ? `Không có kết quả cho "${filter}"`
              : t("chat.empty_title")}
          </p>
        ) : (
          (["today", "yesterday", "week", "older"] as const).map((bucket) => {
            const list = grouped[bucket];
            if (list.length === 0) return null;
            return (
              <div key={bucket}>
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-2 pt-2 pb-1">
                  {BUCKET_LABEL[bucket]}
                </h3>
                {list.map((conv) => (
                  <ConversationRow
                    key={conv.id}
                    conv={conv}
                    isActive={activeId === conv.id}
                    isEditing={editingId === conv.id}
                    editTitle={editTitle}
                    busy={busyId === conv.id}
                    onSelect={() => {
                      if (editingId !== conv.id) setActive(conv.id);
                    }}
                    onChangeTitle={setEditTitle}
                    onStartRename={() => startRename(conv)}
                    onSaveRename={() => void saveRename(conv.id)}
                    onCancelRename={cancelRename}
                    onDelete={() => void handleDelete(conv)}
                  />
                ))}
              </div>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={() => void refresh()}
        className="border-t p-2 text-[10px] text-muted-foreground hover:text-foreground transition-colors text-center"
        title="Refresh list"
      >
        cuonglv_agent_ai · v2.1 · click để refresh
      </button>
    </aside>
  );
}

interface RowProps {
  conv: Conversation;
  isActive: boolean;
  isEditing: boolean;
  editTitle: string;
  busy: boolean;
  onSelect: () => void;
  onChangeTitle: (v: string) => void;
  onStartRename: () => void;
  onSaveRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
}

function ConversationRow({
  conv,
  isActive,
  isEditing,
  editTitle,
  busy,
  onSelect,
  onChangeTitle,
  onStartRename,
  onSaveRename,
  onCancelRename,
  onDelete,
}: RowProps): JSX.Element {
  if (isEditing) {
    return (
      <div className="px-2 py-1.5 rounded-md bg-muted">
        <div className="flex items-center gap-1">
          <Input
            value={editTitle}
            onChange={(e) => onChangeTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveRename();
              else if (e.key === "Escape") onCancelRename();
            }}
            autoFocus
            className="h-7 text-sm flex-1"
          />
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={onSaveRename}
            disabled={busy}
            title="Lưu"
          >
            <Check className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={onCancelRename}
            title="Huỷ"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onSelect}
      className={cn(
        "group w-full text-left px-2.5 py-2 rounded-md text-sm transition-colors cursor-pointer",
        "hover:bg-accent",
        isActive && "bg-accent text-accent-foreground",
        busy && "opacity-50",
      )}
    >
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm">
            {conv.title ?? "Hội thoại không tên"}
          </div>
          {conv.preview ? (
            <div className="text-xs text-muted-foreground truncate">
              {conv.preview}
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            "flex items-center gap-0.5 shrink-0 transition-opacity",
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            onClick={onStartRename}
            disabled={busy}
            title="Đổi tên"
          >
            <PenLine className="size-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-6 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={busy}
            title="Xoá"
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Date bucketing
// ============================================================================

type Bucket = "today" | "yesterday" | "week" | "older";

const BUCKET_LABEL: Record<Bucket, string> = {
  today: "Hôm nay",
  yesterday: "Hôm qua",
  week: "Tuần này",
  older: "Cũ hơn",
};

function groupByDate(items: Conversation[]): Record<Bucket, Conversation[]> {
  const out: Record<Bucket, Conversation[]> = {
    today: [],
    yesterday: [],
    week: [],
    older: [],
  };
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const weekStart = todayStart - 6 * 24 * 60 * 60 * 1000;

  for (const c of items) {
    const ts = new Date(c.last_message_at).getTime();
    if (Number.isNaN(ts)) {
      out.older.push(c);
      continue;
    }
    if (ts >= todayStart) out.today.push(c);
    else if (ts >= yesterdayStart) out.yesterday.push(c);
    else if (ts >= weekStart) out.week.push(c);
    else out.older.push(c);
  }
  return out;
}
